import type { OutlineEntry, VersionInfo } from '@/lib/types/report'
import { sectionTitleMap, shortTime, timelineWindow, versionTitleLabel } from '@/lib/report/versionDiff'
import { cn } from '@/lib/utils'

interface VersionTimelineProps {
  versions: VersionInfo[]
  outline: OutlineEntry[]
  selected: number
  headVersion: number
  isLive: boolean
  onSelect: (version: number) => void
}

/** Horizontal version stepper — viewer chrome, hidden in print. Long histories
 *  collapse to first · … · last five (the selected pill stays visible); pills wear
 *  human section titles with a full label + timestamp tooltip. */
export function VersionTimeline({ versions, outline, selected, headVersion, isLive, onSelect }: VersionTimelineProps) {
  if (versions.length === 0) return null
  const titles = sectionTitleMap(outline)
  const items = timelineWindow(versions, selected)
  return (
    <nav aria-label="Report versions" className="print:hidden flex flex-wrap items-center gap-1.5">
      {items.map((item, i) =>
        item === 'gap' ? (
          <span key={`gap-${i}`} aria-hidden="true" className="px-0.5 text-xs text-muted-foreground/60">
            …
          </span>
        ) : (
          <button
            key={item.version}
            type="button"
            onClick={() => onSelect(item.version)}
            aria-current={item.version === selected ? 'true' : undefined}
            title={`${versionTitleLabel(item, titles)}${shortTime(item.created_at) ? ` · ${shortTime(item.created_at)}` : ''}`}
            className={cn(
              'max-w-44 truncate rounded-full border px-2.5 py-0.5 text-xs transition-colors',
              item.version === selected
                ? 'border-[var(--report-accent)] bg-[var(--report-accent)]/10 text-foreground'
                : 'border-border text-muted-foreground hover:border-[var(--report-accent)]/50',
            )}
          >
            {versionTitleLabel(item, titles)}
          </button>
        ),
      )}
      {isLive && (
        <span className="ml-1 inline-flex items-center gap-1.5 text-xs font-medium text-[var(--report-accent)]">
          <span className="h-2 w-2 animate-pulse rounded-full bg-[var(--report-accent)]" aria-hidden="true" />
          live · v{headVersion}
        </span>
      )}
    </nav>
  )
}
